import AsyncStorage from '@react-native-async-storage/async-storage';

/**
 * Comprehensive Career Coaching Service
 * Provides AI-powered career coaching, interview preparation, resume optimization,
 * skill development tracking, and personalized career guidance
 */
class CareerCoachingService {
  // Storage keys
  static STORAGE_KEYS = {
    COACHING_SESSIONS: 'career_coaching_sessions',
    USER_GOALS: 'career_coaching_goals',
    PROGRESS_TRACKING: 'career_progress_tracking',
    INTERVIEW_HISTORY: 'interview_practice_history',
    RESUME_ANALYSIS: 'resume_analysis_data',
    SKILL_ASSESSMENTS: 'skill_assessment_results',
    CAREER_PLANS: 'career_development_plans',
    COACHING_PREFERENCES: 'coaching_preferences',
    AI_CONVERSATIONS: 'ai_coaching_conversations',
    ACHIEVEMENTS: 'coaching_achievements',
    LEARNING_RESOURCES: 'recommended_learning_resources',
    INDUSTRY_INSIGHTS: 'industry_market_insights'
  };

  // Coaching categories
  static COACHING_CATEGORIES = {
    INTERVIEW_PREP: 'interview_preparation',
    RESUME_OPTIMIZATION: 'resume_optimization',
    CAREER_PLANNING: 'career_planning',
    SKILL_DEVELOPMENT: 'skill_development',
    NETWORKING: 'networking_strategy',
    SALARY_NEGOTIATION: 'salary_negotiation',
    LEADERSHIP: 'leadership_development',
    INDUSTRY_TRANSITION: 'industry_transition'
  };

  // Goal types
  static GOAL_TYPES = {
    SHORT_TERM: 'short_term', // 1-3 months
    MEDIUM_TERM: 'medium_term', // 3-12 months
    LONG_TERM: 'long_term' // 1+ years
  };

  // Interview question types
  static INTERVIEW_TYPES = {
    BEHAVIORAL: 'behavioral',
    TECHNICAL: 'technical',
    SITUATIONAL: 'situational',
    COMPANY_SPECIFIC: 'company_specific',
    INDUSTRY_SPECIFIC: 'industry_specific'
  };

  /**
   * Initialize the career coaching service
   */
  static async initialize() {
    try {
      console.log('🎯 Initializing CareerCoachingService...');
      
      // Ensure all storage keys exist
      for (const [key, storageKey] of Object.entries(this.STORAGE_KEYS)) {
        const data = await AsyncStorage.getItem(storageKey);
        if (!data) {
          await AsyncStorage.setItem(storageKey, JSON.stringify([]));
        }
      }
      
      // Initialize default coaching data if needed
      await this.initializeDefaultData();
      
      console.log('✅ CareerCoachingService initialized successfully');
      return true;
    } catch (error) {
      console.error('❌ Error initializing CareerCoachingService:', error);
      return false;
    }
  }

  /**
   * Initialize default coaching data
   */
  static async initializeDefaultData() {
    try {
      // Check if default goals exist
      const existingGoals = await this.getUserGoals();
      if (existingGoals.length === 0) {
        const defaultGoals = [
          {
            id: 'goal_interview_skills',
            title: 'Master Interview Skills',
            description: 'Improve confidence and performance in job interviews',
            category: this.COACHING_CATEGORIES.INTERVIEW_PREP,
            type: this.GOAL_TYPES.SHORT_TERM,
            progress: 0,
            target: 100,
            status: 'active',
            createdAt: new Date().toISOString(),
            targetDate: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString(), // 90 days
            milestones: [
              { id: 'm1', title: 'Complete behavioral questions practice', completed: false },
              { id: 'm2', title: 'Practice technical questions', completed: false },
              { id: 'm3', title: 'Complete mock interview', completed: false }
            ]
          },
          {
            id: 'goal_resume_optimization',
            title: 'Optimize Resume for ATS',
            description: 'Enhance resume to pass Applicant Tracking Systems',
            category: this.COACHING_CATEGORIES.RESUME_OPTIMIZATION,
            type: this.GOAL_TYPES.SHORT_TERM,
            progress: 0,
            target: 100,
            status: 'active',
            createdAt: new Date().toISOString(),
            targetDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days
            milestones: [
              { id: 'm1', title: 'Complete resume analysis', completed: false },
              { id: 'm2', title: 'Implement optimization suggestions', completed: false },
              { id: 'm3', title: 'Test ATS compatibility', completed: false }
            ]
          },
          {
            id: 'goal_career_planning',
            title: 'Develop 5-Year Career Plan',
            description: 'Create a strategic roadmap for career advancement',
            category: this.COACHING_CATEGORIES.CAREER_PLANNING,
            type: this.GOAL_TYPES.LONG_TERM,
            progress: 0,
            target: 100,
            status: 'active',
            createdAt: new Date().toISOString(),
            targetDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(), // 1 year
            milestones: [
              { id: 'm1', title: 'Complete career assessment', completed: false },
              { id: 'm2', title: 'Identify target roles and companies', completed: false },
              { id: 'm3', title: 'Create skill development plan', completed: false },
              { id: 'm4', title: 'Build professional network', completed: false }
            ]
          }
        ];
        
        await this.saveUserGoals(defaultGoals);
      }

      // Initialize default learning resources
      const existingResources = await this.getLearningResources();
      if (existingResources.length === 0) {
        const defaultResources = [
          {
            id: 'resource_interview_guide',
            title: 'Complete Interview Preparation Guide',
            description: 'Comprehensive guide covering all types of interview questions',
            category: this.COACHING_CATEGORIES.INTERVIEW_PREP,
            type: 'guide',
            difficulty: 'beginner',
            estimatedTime: '2 hours',
            url: 'internal://interview-guide',
            tags: ['interviews', 'behavioral', 'technical'],
            rating: 4.8,
            completedBy: 0
          },
          {
            id: 'resource_resume_templates',
            title: 'ATS-Optimized Resume Templates',
            description: 'Professional resume templates designed to pass ATS systems',
            category: this.COACHING_CATEGORIES.RESUME_OPTIMIZATION,
            type: 'template',
            difficulty: 'beginner',
            estimatedTime: '1 hour',
            url: 'internal://resume-templates',
            tags: ['resume', 'ATS', 'templates'],
            rating: 4.9,
            completedBy: 0
          },
          {
            id: 'resource_career_assessment',
            title: 'Professional Career Assessment Tool',
            description: 'Comprehensive assessment to identify career strengths and opportunities',
            category: this.COACHING_CATEGORIES.CAREER_PLANNING,
            type: 'assessment',
            difficulty: 'intermediate',
            estimatedTime: '45 minutes',
            url: 'internal://career-assessment',
            tags: ['assessment', 'career-planning', 'strengths'],
            rating: 4.7,
            completedBy: 0
          }
        ];
        
        await this.saveLearningResources(defaultResources);
      }

      console.log('✅ Default coaching data initialized');
    } catch (error) {
      console.error('❌ Error initializing default data:', error);
    }
  }

  /**
   * Create a new coaching session
   */
  static async createCoachingSession(sessionData) {
    try {
      const sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      const newSession = {
        id: sessionId,
        type: sessionData.type || 'general_coaching',
        title: sessionData.title || 'Coaching Session',
        description: sessionData.description || '',
        category: sessionData.category || this.COACHING_CATEGORIES.CAREER_PLANNING,
        startTime: new Date().toISOString(),
        endTime: null,
        status: 'active',
        goals: sessionData.goals || [],
        notes: [],
        progress: {
          completed: 0,
          total: sessionData.totalSteps || 1
        },
        aiConversation: [],
        feedback: null,
        rating: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      const sessions = await this.getCoachingSessions();
      sessions.push(newSession);
      await AsyncStorage.setItem(this.STORAGE_KEYS.COACHING_SESSIONS, JSON.stringify(sessions));
      
      console.log('✅ Coaching session created:', sessionId);
      return newSession;
    } catch (error) {
      console.error('❌ Error creating coaching session:', error);
      throw error;
    }
  }

  /**
   * Get all coaching sessions
   */
  static async getCoachingSessions() {
    try {
      const data = await AsyncStorage.getItem(this.STORAGE_KEYS.COACHING_SESSIONS);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('❌ Error getting coaching sessions:', error);
      return [];
    }
  }

  /**
   * Update coaching session
   */
  static async updateCoachingSession(sessionId, updates) {
    try {
      const sessions = await this.getCoachingSessions();
      const sessionIndex = sessions.findIndex(s => s.id === sessionId);
      
      if (sessionIndex !== -1) {
        sessions[sessionIndex] = {
          ...sessions[sessionIndex],
          ...updates,
          updatedAt: new Date().toISOString()
        };
        
        await AsyncStorage.setItem(this.STORAGE_KEYS.COACHING_SESSIONS, JSON.stringify(sessions));
        return sessions[sessionIndex];
      }
      
      return null;
    } catch (error) {
      console.error('❌ Error updating coaching session:', error);
      throw error;
    }
  }

  /**
   * AI-powered coaching conversation
   */
  static async sendMessageToAI(sessionId, message, context = {}) {
    try {
      const userMessage = {
        id: `msg_${Date.now()}`,
        type: 'user',
        content: message,
        timestamp: new Date().toISOString(),
        context
      };

      // Generate AI response based on context and message
      const aiResponse = await this.generateAIResponse(message, context);

      // Save conversation to session
      const session = await this.getCoachingSessionById(sessionId);
      if (session) {
        session.aiConversation.push(userMessage, aiResponse);
        await this.updateCoachingSession(sessionId, { aiConversation: session.aiConversation });
      }

      return { userMessage, aiResponse };
    } catch (error) {
      console.error('❌ Error in AI conversation:', error);
      throw error;
    }
  }

  /**
   * Generate AI coaching response
   */
  static async generateAIResponse(message, context) {
    try {
      const messageLower = message.toLowerCase();
      let response = '';
      let suggestions = [];
      let actionItems = [];

      // Analyze message intent and generate appropriate response
      if (messageLower.includes('interview') || messageLower.includes('prepare')) {
        response = this.generateInterviewCoachingResponse(message, context);
        suggestions = ['Start mock interview', 'Practice behavioral questions', 'Review common questions', 'Get feedback'];
        actionItems = [
          { type: 'start_interview_practice', label: 'Begin Interview Practice' },
          { type: 'view_questions', label: 'View Question Bank' }
        ];
      } else if (messageLower.includes('resume') || messageLower.includes('cv')) {
        response = this.generateResumeCoachingResponse(message, context);
        suggestions = ['Analyze my resume', 'ATS optimization tips', 'Format improvements', 'Content suggestions'];
        actionItems = [
          { type: 'analyze_resume', label: 'Start Resume Analysis' },
          { type: 'view_templates', label: 'View Templates' }
        ];
      } else if (messageLower.includes('career') || messageLower.includes('plan') || messageLower.includes('goal')) {
        response = this.generateCareerPlanningResponse(message, context);
        suggestions = ['Set career goals', 'Skill gap analysis', 'Industry insights', 'Networking strategy'];
        actionItems = [
          { type: 'create_career_plan', label: 'Create Career Plan' },
          { type: 'assess_skills', label: 'Assess Skills' }
        ];
      } else if (messageLower.includes('skill') || messageLower.includes('learn') || messageLower.includes('develop')) {
        response = this.generateSkillDevelopmentResponse(message, context);
        suggestions = ['Skill assessment', 'Learning resources', 'Certification paths', 'Practice projects'];
        actionItems = [
          { type: 'skill_assessment', label: 'Take Skill Assessment' },
          { type: 'view_resources', label: 'Browse Resources' }
        ];
      } else if (messageLower.includes('salary') || messageLower.includes('negotiate') || messageLower.includes('compensation')) {
        response = this.generateSalaryNegotiationResponse(message, context);
        suggestions = ['Research market rates', 'Negotiation strategies', 'Value proposition', 'Timing advice'];
        actionItems = [
          { type: 'salary_research', label: 'Research Salaries' },
          { type: 'negotiation_guide', label: 'Negotiation Guide' }
        ];
      } else {
        response = `I'm here to help you with your career development! I can assist with interview preparation, resume optimization, career planning, skill development, and more. What specific area would you like to focus on today?`;
        suggestions = ['Interview preparation', 'Resume review', 'Career planning', 'Skill development'];
        actionItems = [
          { type: 'general_assessment', label: 'Career Assessment' },
          { type: 'view_dashboard', label: 'View Dashboard' }
        ];
      }

      return {
        id: `msg_${Date.now()}`,
        type: 'ai',
        content: response,
        timestamp: new Date().toISOString(),
        suggestions,
        actionItems,
        context: {
          category: this.determineMessageCategory(message),
          confidence: 0.85,
          followUpRecommended: true
        }
      };
    } catch (error) {
      console.error('❌ Error generating AI response:', error);
      return {
        id: `msg_${Date.now()}`,
        type: 'ai',
        content: 'I apologize, but I encountered an issue processing your message. Please try again or rephrase your question.',
        timestamp: new Date().toISOString(),
        suggestions: ['Try again', 'Contact support'],
        actionItems: [],
        context: { error: true }
      };
    }
  }

  /**
   * Generate interview coaching response
   */
  static generateInterviewCoachingResponse(message, context) {
    const responses = [
      `Excellent! Interview preparation is crucial for career success. Based on your profile and the roles you're targeting, I recommend starting with behavioral questions using the STAR method (Situation, Task, Action, Result). This framework helps you structure compelling answers that showcase your achievements.`,
      
      `Great choice! Let's work on your interview skills. I suggest we begin with a mock interview session where I'll ask you common questions for your field. This will help identify areas for improvement and build your confidence. Would you like to start with general questions or focus on a specific company?`,
      
      `Interview preparation is one of the most impactful investments in your career. I can help you practice different types of questions - behavioral, technical, and situational. We'll also work on your body language, tone, and how to handle difficult questions. What type of role are you preparing for?`
    ];
    
    return responses[Math.floor(Math.random() * responses.length)];
  }

  /**
   * Generate resume coaching response
   */
  static generateResumeCoachingResponse(message, context) {
    const responses = [
      `Perfect! Resume optimization can significantly increase your interview chances. I'll analyze your resume for ATS compatibility, keyword optimization, and overall impact. We'll focus on quantifying your achievements, improving formatting, and ensuring your key skills are prominently featured.`,
      
      `Great thinking! Your resume is often the first impression you make. I can help you create a compelling narrative that highlights your unique value proposition. We'll work on tailoring your resume for specific roles and industries while maintaining ATS compatibility.`,
      
      `Excellent focus area! A well-optimized resume can increase your response rate by up to 40%. I'll help you identify the most impactful achievements, use action verbs effectively, and structure your content for maximum readability by both ATS systems and human recruiters.`
    ];
    
    return responses[Math.floor(Math.random() * responses.length)];
  }

  /**
   * Generate career planning response
   */
  static generateCareerPlanningResponse(message, context) {
    const responses = [
      `Fantastic! Strategic career planning is essential for long-term success. I'll help you identify your career goals, map out potential paths, and create actionable steps to achieve them. We'll consider your strengths, interests, market trends, and growth opportunities in your field.`,
      
      `Excellent approach! Career planning involves understanding where you want to go and creating a roadmap to get there. I can help you assess your current position, identify target roles, understand skill requirements, and develop a timeline for achieving your goals.`,
      
      `Great initiative! A well-structured career plan can accelerate your professional growth significantly. We'll work on setting SMART goals, identifying key milestones, building necessary skills, and creating strategies for networking and professional development.`
    ];
    
    return responses[Math.floor(Math.random() * responses.length)];
  }

  /**
   * Generate skill development response
   */
  static generateSkillDevelopmentResponse(message, context) {
    const responses = [
      `Excellent focus! Continuous skill development is crucial in today's rapidly evolving job market. I'll help you identify skill gaps, recommend learning resources, and create a development plan that aligns with your career goals and industry demands.`,
      
      `Perfect mindset! Skill development is an investment that pays dividends throughout your career. I can assess your current skills, identify high-impact areas for improvement, and suggest practical ways to build expertise through courses, projects, and certifications.`,
      
      `Great approach! The most successful professionals are lifelong learners. I'll help you create a personalized learning path that balances technical skills, soft skills, and industry knowledge. We'll focus on skills that will have the biggest impact on your career trajectory.`
    ];
    
    return responses[Math.floor(Math.random() * responses.length)];
  }

  /**
   * Generate salary negotiation response
   */
  static generateSalaryNegotiationResponse(message, context) {
    const responses = [
      `Smart thinking! Salary negotiation is a critical skill that can significantly impact your lifetime earnings. I'll help you research market rates, build a compelling case for your value, and develop negotiation strategies that work. Preparation is key to successful negotiations.`,
      
      `Excellent topic! Many professionals leave money on the table by not negotiating effectively. I can guide you through market research, help you articulate your value proposition, and teach you negotiation techniques that lead to better outcomes while maintaining positive relationships.`,
      
      `Great focus area! Effective salary negotiation requires preparation, timing, and strategy. I'll help you understand your market value, identify the right time to negotiate, and develop persuasive arguments based on your achievements and market data.`
    ];
    
    return responses[Math.floor(Math.random() * responses.length)];
  }

  /**
   * Determine message category for better context
   */
  static determineMessageCategory(message) {
    const messageLower = message.toLowerCase();
    
    if (messageLower.includes('interview')) return this.COACHING_CATEGORIES.INTERVIEW_PREP;
    if (messageLower.includes('resume') || messageLower.includes('cv')) return this.COACHING_CATEGORIES.RESUME_OPTIMIZATION;
    if (messageLower.includes('career') || messageLower.includes('plan')) return this.COACHING_CATEGORIES.CAREER_PLANNING;
    if (messageLower.includes('skill') || messageLower.includes('learn')) return this.COACHING_CATEGORIES.SKILL_DEVELOPMENT;
    if (messageLower.includes('network')) return this.COACHING_CATEGORIES.NETWORKING;
    if (messageLower.includes('salary') || messageLower.includes('negotiate')) return this.COACHING_CATEGORIES.SALARY_NEGOTIATION;
    if (messageLower.includes('lead')) return this.COACHING_CATEGORIES.LEADERSHIP;
    
    return 'general';
  }

  /**
   * Get coaching session by ID
   */
  static async getCoachingSessionById(sessionId) {
    try {
      const sessions = await this.getCoachingSessions();
      return sessions.find(s => s.id === sessionId) || null;
    } catch (error) {
      console.error('❌ Error getting coaching session by ID:', error);
      return null;
    }
  }

  /**
   * User Goals Management
   */
  static async getUserGoals() {
    try {
      const data = await AsyncStorage.getItem(this.STORAGE_KEYS.USER_GOALS);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('❌ Error getting user goals:', error);
      return [];
    }
  }

  static async saveUserGoals(goals) {
    try {
      await AsyncStorage.setItem(this.STORAGE_KEYS.USER_GOALS, JSON.stringify(goals));
      return true;
    } catch (error) {
      console.error('❌ Error saving user goals:', error);
      return false;
    }
  }

  static async createUserGoal(goalData) {
    try {
      const goalId = `goal_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      const newGoal = {
        id: goalId,
        title: goalData.title,
        description: goalData.description,
        category: goalData.category,
        type: goalData.type || this.GOAL_TYPES.SHORT_TERM,
        progress: 0,
        target: goalData.target || 100,
        status: 'active',
        createdAt: new Date().toISOString(),
        targetDate: goalData.targetDate,
        milestones: goalData.milestones || [],
        tags: goalData.tags || [],
        priority: goalData.priority || 'medium'
      };

      const goals = await this.getUserGoals();
      goals.push(newGoal);
      await this.saveUserGoals(goals);
      
      return newGoal;
    } catch (error) {
      console.error('❌ Error creating user goal:', error);
      throw error;
    }
  }

  static async updateGoalProgress(goalId, progress, milestoneId = null) {
    try {
      const goals = await this.getUserGoals();
      const goalIndex = goals.findIndex(g => g.id === goalId);
      
      if (goalIndex !== -1) {
        goals[goalIndex].progress = Math.min(progress, goals[goalIndex].target);
        goals[goalIndex].updatedAt = new Date().toISOString();
        
        // Update milestone if specified
        if (milestoneId) {
          const milestone = goals[goalIndex].milestones.find(m => m.id === milestoneId);
          if (milestone) {
            milestone.completed = true;
            milestone.completedAt = new Date().toISOString();
          }
        }
        
        // Check if goal is completed
        if (goals[goalIndex].progress >= goals[goalIndex].target) {
          goals[goalIndex].status = 'completed';
          goals[goalIndex].completedAt = new Date().toISOString();
        }
        
        await this.saveUserGoals(goals);
        return goals[goalIndex];
      }
      
      return null;
    } catch (error) {
      console.error('❌ Error updating goal progress:', error);
      throw error;
    }
  }

  /**
   * Interview Practice Management
   */
  static async getInterviewQuestions(category = null, difficulty = null) {
    try {
      const allQuestions = [
        // Behavioral Questions
        {
          id: 'bq_001',
          question: 'Tell me about yourself and your professional background.',
          category: this.INTERVIEW_TYPES.BEHAVIORAL,
          difficulty: 'beginner',
          tips: 'Focus on your professional journey, key achievements, and what makes you unique. Keep it concise and relevant to the role.',
          expectedDuration: 120,
          tags: ['introduction', 'background', 'summary'],
          sampleAnswer: 'Structure your answer chronologically, highlighting key career milestones and achievements that relate to the target role.'
        },
        {
          id: 'bq_002',
          question: 'Describe a challenging project you worked on and how you overcame obstacles.',
          category: this.INTERVIEW_TYPES.BEHAVIORAL,
          difficulty: 'intermediate',
          tips: 'Use the STAR method: Situation, Task, Action, Result. Focus on your problem-solving skills and leadership.',
          expectedDuration: 180,
          tags: ['problem-solving', 'leadership', 'challenges'],
          sampleAnswer: 'Choose a specific example that demonstrates your ability to handle complexity and deliver results under pressure.'
        },
        {
          id: 'bq_003',
          question: 'How do you handle conflict with team members or colleagues?',
          category: this.INTERVIEW_TYPES.BEHAVIORAL,
          difficulty: 'intermediate',
          tips: 'Show emotional intelligence, communication skills, and ability to find win-win solutions.',
          expectedDuration: 150,
          tags: ['conflict-resolution', 'teamwork', 'communication'],
          sampleAnswer: 'Demonstrate your ability to listen, understand different perspectives, and find collaborative solutions.'
        },
        // Technical Questions (General)
        {
          id: 'tq_001',
          question: 'Explain your approach to problem-solving when faced with a technical challenge.',
          category: this.INTERVIEW_TYPES.TECHNICAL,
          difficulty: 'intermediate',
          tips: 'Walk through your systematic approach: understanding the problem, researching solutions, testing, and implementation.',
          expectedDuration: 200,
          tags: ['problem-solving', 'methodology', 'technical-thinking'],
          sampleAnswer: 'Outline a clear, logical process that shows your analytical thinking and attention to detail.'
        },
        {
          id: 'tq_002',
          question: 'How do you stay current with industry trends and new technologies?',
          category: this.INTERVIEW_TYPES.TECHNICAL,
          difficulty: 'beginner',
          tips: 'Mention specific resources, communities, and learning methods you use regularly.',
          expectedDuration: 120,
          tags: ['continuous-learning', 'industry-knowledge', 'professional-development'],
          sampleAnswer: 'Share specific examples of how you\'ve applied new knowledge to improve your work or solve problems.'
        },
        // Situational Questions
        {
          id: 'sq_001',
          question: 'How would you handle a situation where you disagree with your manager\'s decision?',
          category: this.INTERVIEW_TYPES.SITUATIONAL,
          difficulty: 'advanced',
          tips: 'Show respect for hierarchy while demonstrating your ability to provide constructive feedback.',
          expectedDuration: 160,
          tags: ['management', 'communication', 'professionalism'],
          sampleAnswer: 'Emphasize respectful communication, data-driven arguments, and willingness to support team decisions.'
        },
        {
          id: 'sq_002',
          question: 'What would you do if you realized you made a significant error in your work?',
          category: this.INTERVIEW_TYPES.SITUATIONAL,
          difficulty: 'intermediate',
          tips: 'Show accountability, problem-solving skills, and commitment to learning from mistakes.',
          expectedDuration: 140,
          tags: ['accountability', 'error-handling', 'integrity'],
          sampleAnswer: 'Demonstrate immediate action to fix the issue, transparent communication, and steps to prevent recurrence.'
        }
      ];

      let filteredQuestions = allQuestions;
      
      if (category) {
        filteredQuestions = filteredQuestions.filter(q => q.category === category);
      }
      
      if (difficulty) {
        filteredQuestions = filteredQuestions.filter(q => q.difficulty === difficulty);
      }
      
      return filteredQuestions;
    } catch (error) {
      console.error('❌ Error getting interview questions:', error);
      return [];
    }
  }

  static async saveInterviewPracticeSession(sessionData) {
    try {
      const sessions = await this.getInterviewHistory();
      const newSession = {
        id: `interview_${Date.now()}`,
        ...sessionData,
        createdAt: new Date().toISOString()
      };
      
      sessions.push(newSession);
      await AsyncStorage.setItem(this.STORAGE_KEYS.INTERVIEW_HISTORY, JSON.stringify(sessions));
      
      return newSession;
    } catch (error) {
      console.error('❌ Error saving interview practice session:', error);
      throw error;
    }
  }

  static async getInterviewHistory() {
    try {
      const data = await AsyncStorage.getItem(this.STORAGE_KEYS.INTERVIEW_HISTORY);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('❌ Error getting interview history:', error);
      return [];
    }
  }

  /**
   * Learning Resources Management
   */
  static async getLearningResources(category = null) {
    try {
      const data = await AsyncStorage.getItem(this.STORAGE_KEYS.LEARNING_RESOURCES);
      let resources = data ? JSON.parse(data) : [];
      
      if (category) {
        resources = resources.filter(r => r.category === category);
      }
      
      return resources;
    } catch (error) {
      console.error('❌ Error getting learning resources:', error);
      return [];
    }
  }

  static async saveLearningResources(resources) {
    try {
      await AsyncStorage.setItem(this.STORAGE_KEYS.LEARNING_RESOURCES, JSON.stringify(resources));
      return true;
    } catch (error) {
      console.error('❌ Error saving learning resources:', error);
      return false;
    }
  }

  /**
   * Progress Tracking
   */
  static async getProgressTracking() {
    try {
      const data = await AsyncStorage.getItem(this.STORAGE_KEYS.PROGRESS_TRACKING);
      return data ? JSON.parse(data) : {
        overallProgress: 0,
        categoryProgress: {},
        weeklyActivity: [],
        achievements: [],
        streaks: {
          current: 0,
          longest: 0
        }
      };
    } catch (error) {
      console.error('❌ Error getting progress tracking:', error);
      return {};
    }
  }

  static async updateProgressTracking(updates) {
    try {
      const currentProgress = await this.getProgressTracking();
      const updatedProgress = { ...currentProgress, ...updates };
      
      await AsyncStorage.setItem(this.STORAGE_KEYS.PROGRESS_TRACKING, JSON.stringify(updatedProgress));
      return updatedProgress;
    } catch (error) {
      console.error('❌ Error updating progress tracking:', error);
      throw error;
    }
  }

  /**
   * Analytics and Insights
   */
  static async generateCoachingAnalytics() {
    try {
      const [goals, sessions, progress, interviewHistory] = await Promise.all([
        this.getUserGoals(),
        this.getCoachingSessions(),
        this.getProgressTracking(),
        this.getInterviewHistory()
      ]);

      const analytics = {
        overview: {
          totalGoals: goals.length,
          activeGoals: goals.filter(g => g.status === 'active').length,
          completedGoals: goals.filter(g => g.status === 'completed').length,
          totalSessions: sessions.length,
          activeSessions: sessions.filter(s => s.status === 'active').length,
          completedSessions: sessions.filter(s => s.status === 'completed').length
        },
        progress: {
          overallProgress: this.calculateOverallProgress(goals),
          categoryBreakdown: this.calculateCategoryProgress(goals),
          weeklyActivity: progress.weeklyActivity || [],
          monthlyTrend: this.calculateMonthlyTrend(sessions)
        },
        achievements: {
          recent: progress.achievements?.slice(-5) || [],
          total: progress.achievements?.length || 0,
          streaks: progress.streaks || { current: 0, longest: 0 }
        },
        recommendations: this.generateRecommendations(goals, sessions, progress),
        insights: this.generateInsights(goals, sessions, interviewHistory)
      };

      return analytics;
    } catch (error) {
      console.error('❌ Error generating coaching analytics:', error);
      return null;
    }
  }

  static calculateOverallProgress(goals) {
    if (goals.length === 0) return 0;
    
    const totalProgress = goals.reduce((sum, goal) => sum + (goal.progress / goal.target) * 100, 0);
    return Math.round(totalProgress / goals.length);
  }

  static calculateCategoryProgress(goals) {
    const categoryProgress = {};
    
    Object.values(this.COACHING_CATEGORIES).forEach(category => {
      const categoryGoals = goals.filter(g => g.category === category);
      if (categoryGoals.length > 0) {
        const avgProgress = categoryGoals.reduce((sum, goal) => sum + (goal.progress / goal.target) * 100, 0) / categoryGoals.length;
        categoryProgress[category] = Math.round(avgProgress);
      }
    });
    
    return categoryProgress;
  }

  static calculateMonthlyTrend(sessions) {
    // Calculate session activity over the last 6 months
    const months = [];
    const now = new Date();
    
    for (let i = 5; i >= 0; i--) {
      const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthSessions = sessions.filter(session => {
        const sessionDate = new Date(session.createdAt);
        return sessionDate.getMonth() === monthDate.getMonth() && 
               sessionDate.getFullYear() === monthDate.getFullYear();
      });
      
      months.push({
        month: monthDate.toLocaleDateString('en-US', { month: 'short' }),
        sessions: monthSessions.length,
        completedSessions: monthSessions.filter(s => s.status === 'completed').length
      });
    }
    
    return months;
  }

  static generateRecommendations(goals, sessions, progress) {
    const recommendations = [];
    
    // Goal-based recommendations
    const activeGoals = goals.filter(g => g.status === 'active');
    if (activeGoals.length === 0) {
      recommendations.push({
        type: 'goal_setting',
        title: 'Set Your First Career Goal',
        description: 'Start your coaching journey by setting a specific, measurable career goal.',
        priority: 'high',
        action: 'create_goal'
      });
    }
    
    // Progress-based recommendations
    const stagnantGoals = activeGoals.filter(g => g.progress < 10);
    if (stagnantGoals.length > 0) {
      recommendations.push({
        type: 'progress_boost',
        title: 'Kickstart Your Progress',
        description: `You have ${stagnantGoals.length} goal(s) with minimal progress. Let's create action plans to get moving!`,
        priority: 'medium',
        action: 'create_action_plan'
      });
    }
    
    // Session-based recommendations
    const recentSessions = sessions.filter(s => {
      const sessionDate = new Date(s.createdAt);
      const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
      return sessionDate > weekAgo;
    });
    
    if (recentSessions.length === 0) {
      recommendations.push({
        type: 'engagement',
        title: 'Schedule a Coaching Session',
        description: 'It\'s been a while since your last session. Regular coaching leads to better results!',
        priority: 'medium',
        action: 'start_session'
      });
    }
    
    return recommendations.slice(0, 3); // Return top 3 recommendations
  }

  static generateInsights(goals, sessions, interviewHistory) {
    const insights = [];
    
    // Goal completion insights
    const completedGoals = goals.filter(g => g.status === 'completed');
    if (completedGoals.length > 0) {
      const avgCompletionTime = completedGoals.reduce((sum, goal) => {
        const created = new Date(goal.createdAt);
        const completed = new Date(goal.completedAt);
        return sum + (completed - created);
      }, 0) / completedGoals.length;
      
      const avgDays = Math.round(avgCompletionTime / (1000 * 60 * 60 * 24));
      insights.push({
        type: 'completion_rate',
        title: 'Goal Completion Pattern',
        description: `You typically complete goals in ${avgDays} days. This shows great consistency!`,
        icon: 'trending-up'
      });
    }
    
    // Interview practice insights
    if (interviewHistory.length > 0) {
      const avgScore = interviewHistory.reduce((sum, session) => sum + (session.score || 0), 0) / interviewHistory.length;
      insights.push({
        type: 'interview_performance',
        title: 'Interview Skills Progress',
        description: `Your average interview practice score is ${Math.round(avgScore)}%. Keep practicing to improve!`,
        icon: 'school'
      });
    }
    
    // Activity insights
    const activeDays = sessions.reduce((days, session) => {
      const sessionDate = new Date(session.createdAt).toDateString();
      if (!days.includes(sessionDate)) {
        days.push(sessionDate);
      }
      return days;
    }, []);
    
    if (activeDays.length > 0) {
      insights.push({
        type: 'activity_pattern',
        title: 'Coaching Engagement',
        description: `You've been active on ${activeDays.length} different days. Consistency is key to success!`,
        icon: 'calendar'
      });
    }
    
    return insights;
  }

  /**
   * Export coaching data for backup or analysis
   */
  static async exportCoachingData() {
    try {
      const [goals, sessions, progress, interviewHistory, resources] = await Promise.all([
        this.getUserGoals(),
        this.getCoachingSessions(),
        this.getProgressTracking(),
        this.getInterviewHistory(),
        this.getLearningResources()
      ]);

      const exportData = {
        goals,
        sessions,
        progress,
        interviewHistory,
        resources,
        exportDate: new Date().toISOString(),
        version: '1.0'
      };

      return exportData;
    } catch (error) {
      console.error('❌ Error exporting coaching data:', error);
      return null;
    }
  }

  /**
   * Import coaching data from backup
   */
  static async importCoachingData(importData) {
    try {
      if (!importData || !importData.goals) {
        throw new Error('Invalid import data');
      }

      // Backup current data
      const currentData = await this.exportCoachingData();
      await AsyncStorage.setItem('coaching_backup_before_import', JSON.stringify(currentData));

      // Import data
      if (importData.goals) await this.saveUserGoals(importData.goals);
      if (importData.sessions) await AsyncStorage.setItem(this.STORAGE_KEYS.COACHING_SESSIONS, JSON.stringify(importData.sessions));
      if (importData.progress) await AsyncStorage.setItem(this.STORAGE_KEYS.PROGRESS_TRACKING, JSON.stringify(importData.progress));
      if (importData.interviewHistory) await AsyncStorage.setItem(this.STORAGE_KEYS.INTERVIEW_HISTORY, JSON.stringify(importData.interviewHistory));
      if (importData.resources) await this.saveLearningResources(importData.resources);

      console.log('✅ Coaching data imported successfully');
      return true;
    } catch (error) {
      console.error('❌ Error importing coaching data:', error);
      return false;
    }
  }
}

export default CareerCoachingService;
